import React, { useState, useEffect } from 'react';
import {
  TextField, Button, Box,
  InputLabel, FormControl, Select, MenuItem, Alert, CircularProgress
} from '@mui/material';
import { Tag } from 'lucide-react';
import { AdminDialog, AdminDialogHeader, AdminDialogContent, AdminDialogActions } from './common/modal';
import { useDispatch } from 'react-redux';
import { createAdminBrand, updateAdminBrand } from '../redux/slices/catalogSlice';

const BrandModal = ({ open, onClose, brand, categories, onSuccess }) => {
  const dispatch = useDispatch();
  const isEdit = !!brand;
  
  const [formData, setFormData] = useState({
    brand_name: '',
    category_id: '',
    brand_status: 'Activated'
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (brand && open) {
      setFormData({
        brand_name: brand.brand_name || '',
        category_id: brand.category_id || '',
        brand_status: brand.brand_status || 'Activated'
      });
    } else if (open) {
      setFormData({ 
        brand_name: '', 
        category_id: '', 
        brand_status: 'Activated' 
      });
    }
    setError('');
  }, [brand, open]);

  const handleChange = (e) => {
    const value = e.target.name === 'category_id' || e.target.name === 'brand_status'
      ? e.target.value
      : e.target.value.trimStart();
    setFormData({ ...formData, [e.target.name]: value });
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.brand_name.trim()) {
      setError('Brand name is required');
      return;
    }
    if (!formData.category_id) {
      setError('Category is required');
      return;
    }
    
    setLoading(true);
    try {
      const trimmedData = {
        ...formData,
        brand_name: formData.brand_name.trim(),
      };
      if (isEdit) {
        await dispatch(updateAdminBrand({ id: brand._id, data: trimmedData })).unwrap();
      } else {
        await dispatch(createAdminBrand(trimmedData)).unwrap();
      }
      onSuccess();
      onClose();
    } catch (err) {
      setError(err || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AdminDialog
      open={open}
      onClose={!loading ? onClose : undefined}
      maxWidth="sm"
      fullWidth
    >
      <AdminDialogHeader
        title={isEdit ? 'Edit Brand' : 'Create New Brand'}
        subtitle={isEdit ? 'Update brand details and category association' : 'Add a new brand to the product catalog'}
        icon={Tag}
        iconColor="primary"
        onClose={!loading ? onClose : undefined}
      />
      
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
        <AdminDialogContent dividers>
          {error && (
            <Alert severity="error" sx={{ mb: 2.5, borderRadius: 2 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mt: 0.5 }}>
            <TextField
              label="Brand Name"
              name="brand_name"
              value={formData.brand_name}
              onChange={handleChange}
              fullWidth
              required
              placeholder="e.g. Samsung, Nike, Puma"
            />
            
            <FormControl fullWidth required>
              <InputLabel>Category</InputLabel>
              <Select
                name="category_id"
                value={formData.category_id}
                label="Category"
                onChange={handleChange}
              >
                {categories?.map((cat) => (
                  <MenuItem key={cat._id} value={cat._id}>{cat.category_name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                name="brand_status"
                value={formData.brand_status}
                label="Status"
                onChange={handleChange}
              >
                <MenuItem value="Activated">Activated</MenuItem>
                <MenuItem value="Deactivated">Deactivated</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </AdminDialogContent>
        
        <AdminDialogActions>
          <Button onClick={onClose} variant="outlined" disabled={loading} sx={{ fontWeight: 600 }}>
            Cancel
          </Button>
          <Button 
            type="submit" 
            variant="contained" 
            color="primary"
            disabled={loading}
            startIcon={loading && <CircularProgress size={16} color="inherit" />}
            sx={{ px: 3, borderRadius: 2, fontWeight: 700 }}
          >
            {loading ? 'Saving...' : isEdit ? 'Save Changes' : 'Create Brand'}
          </Button>
        </AdminDialogActions>
      </form>
    </AdminDialog>
  );
};

export default BrandModal;

